import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Tooltip,
  Autocomplete,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { ReactComponent as DeleteIcon } from '../../../assets/SVG/trash.svg';
import { ReactComponent as CopyIcon } from '../../../assets/SVG/Duplicate-Ajustment.svg';
import { ReactComponent as CopyIconDisabled } from '../../../assets/SVG/duplicate_icon_grey.svg';
import {
  PharmacyItem as FormPharmacyItem,
  PackageDetail,
  tableHeaderRowStyles,
} from '../../Billing/Pharmacy/newPharmacy/newPharmacyForm.helper';
import { hasAnyQtyError } from '../dispenseHelper';
import { calculateEffectivePackageQty, calculateNetPricePerUnit, calcFinancials } from '../directDispenseFormHelpers';
import useCurrency from '../../../components/CurrencyProvider';
import ItemSearchField, { PharmacyItem as ApiPharmacyItem } from '../../Billing/Pharmacy/newPharmacy/ItemSearchField';
import styles from '../../../pages/Inventory/Common/Inventory.module.css';
import { useTranslation } from 'react-i18next';

export { hasAnyQtyError };

export interface PharmacyItemsTableProps {
  items: FormPharmacyItem[];
  onSelectChange: (index: number) => void;
  /** Callback to select/deselect all items */
  onSelectAll?: (selectAll: boolean) => void;
  onRemoveItem?: (index: number) => void;
  onFieldChange: (index: number, field: keyof FormPharmacyItem, value: string | number) => void;
  /** Batch update for multiple fields at once - use this for calculated fields */
  onItemUpdate?: (index: number, updates: Partial<FormPharmacyItem>) => void;
  errors?: Array<Record<string, string>>;
  touched?: Array<Record<string, boolean>>;
  /** If true, shows delete action column */
  showDeleteAction?: boolean;
  /** If true, shows read-only mode without editable fields */
  readOnly?: boolean;
  /** If true, shows table header row. Defaults to true */
  showHeader?: boolean;
  /** Callback to send qtyError to parent */
  onQtyErrorChange?: (hasError: boolean) => void;
  /** Callback when user selects a replacement item for rows with no available batches */
  onItemReplace?: (index: number, item: ApiPharmacyItem) => void;
  /** Clinic ID for item search (used when replacing items) */
  clinicId?: number;
  /** Department/Store ID for item search (used when replacing items) */
  deptId?: number | null;
  /** Patient ID for item search (used when replacing items) */
  patientId?: number | null;
  /** Callback when package is selected for an item */
  onPackageChange?: (index: number, packageDetail: PackageDetail | null) => void;
  /** If true, disables batch selection dropdown (for QR scan mode) */
  disableBatchSelection?: boolean;
  /** Callback when user clicks duplicate button to add another line for same item */
  onDuplicateItem?: (index: number) => void;
}

/**
 * Helper to calculate concession values and recalculate taxes/net amount
 * 
 * EXCEL-STYLE CALCULATION RATIONALE:
 * We round at each intermediate step per unit before multiplying by quantity.
 * This approach is used to:
 * 1. Match legacy Excel-based invoice calculations that users verify against
 * 2. Prevent cumulative floating-point rounding errors (e.g., 0.1 + 0.2 !== 0.3 in JS)
 * 3. Ensure line items sum exactly to totals (no "missing penny" discrepancies)
 * 
 * Alternative: Rounding only the final result is simpler but can produce values
 * that differ by 1-2 cents from Excel/manual calculations, causing reconciliation issues.
 * 
 * Calculation Steps:
 * 1. sellingPricePerUnit = round(baseMRP / (1 + totalTaxPercent / 100), 2)
 * 2. discountPerUnit = round(sellingPricePerUnit * (discountPercent / 100), 2)
 * 3. taxablePerUnit = round(sellingPricePerUnit - discountPerUnit, 2)
 * 4. totalTaxable = round(taxablePerUnit * quantity, 2)
 * 5. taxAmount = round(totalTaxable * (taxPercent / 100), 2)
 * 6. netAmount = round(totalTaxable + sumOfAllTaxAmounts, 2)
 */
const round2 = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

const calculateConcessionValues = (
  item: FormPharmacyItem,
  newPercent: number
): Partial<FormPharmacyItem> => {
  const baseTaxes = item.taxes && item.taxes.length > 0 ? item.taxes : [];
  const totalTaxPercent = baseTaxes.reduce((sum, t) => sum + (t.percent || 0), 0);
  const qty = item.billedQty || 1;
  
  // 1. Selling Price Per Unit (Tax Exclusive) - ROUND AT UNIT LEVEL
  const sellingPricePerUnit = round2(item.baseMRP / (1 + totalTaxPercent / 100));
  
  // 2. Discount Per Unit - ROUND AT UNIT LEVEL
  const discountPerUnit = round2(sellingPricePerUnit * (newPercent / 100));
  
  // 3. Taxable Per Unit - ROUND AT UNIT LEVEL (ensure never negative)
  const taxablePerUnit = round2(Math.max(0, sellingPricePerUnit - discountPerUnit));
  
  // 4. Total Concession Amount = Discount Per Unit × Qty
  const concessionAmount = round2(discountPerUnit * qty);
  
  // 5. Total Taxable Amount = Taxable Per Unit × Qty
  const totalTaxableAmount = round2(taxablePerUnit * qty);
  
  // 6. Calculate each tax separately on taxable amount
  const adjustedTaxes = baseTaxes.map(t => ({
    ...t,
    amount: round2(Math.max(0, totalTaxableAmount * (t.percent || 0) / 100))
  }));
  const totalTaxAmount = round2(adjustedTaxes.reduce((sum, t) => sum + t.amount, 0));
  
  // 7. Net = Taxable Amount + Taxes (ensure never negative)
  let newNetAmount = round2(Math.max(0, totalTaxableAmount + totalTaxAmount));
  
  // 8. Cap net amount to not exceed totalMRP (prevents rounding artifacts)
  const totalMRP = round2(item.baseMRP * qty);
  if (newNetAmount > totalMRP) {
    newNetAmount = totalMRP;
  }
  
  return {
    concessionPercent: newPercent,
    concessionAmount: concessionAmount,
    totalSellingPrice: totalTaxableAmount,
    taxes: adjustedTaxes,
    netAmount: newNetAmount,
  };
};

/**
 * Helper to calculate concession values from total amount (inverse calculation)
 * Given a total concession amount, calculate the percent and update all related values.
 * Uses Excel-style rounding at each intermediate step to match legacy calculations
 * and prevent cumulative floating-point errors. See calculateConcessionValues for full rationale.
 */
const calculateConcessionFromAmount = (
  item: FormPharmacyItem,
  newAmount: number
): Partial<FormPharmacyItem> => {
  const baseTaxes = item.taxes && item.taxes.length > 0 ? item.taxes : [];
  const totalTaxPercent = baseTaxes.reduce((sum, t) => sum + (t.percent || 0), 0);
  const qty = item.billedQty || 1;
  
  // 1. Selling Price Per Unit (Tax Exclusive) - ROUND AT UNIT LEVEL
  const sellingPricePerUnit = round2(item.baseMRP / (1 + totalTaxPercent / 100));
  
  // 2. Calculate discount per unit from total amount
  const discountPerUnit = round2(qty > 0 ? newAmount / qty : 0);
  
  // 3. Calculate percent from discount per unit (based on selling price)
  const newPercent = sellingPricePerUnit > 0 
    ? Math.min(100, round2((discountPerUnit / sellingPricePerUnit) * 100)) 
    : 0;
  
  // 4. Taxable Per Unit - ROUND AT UNIT LEVEL (ensure never negative)
  const taxablePerUnit = round2(Math.max(0, sellingPricePerUnit - discountPerUnit));
  
  // 5. Total Taxable Amount = Taxable Per Unit × Qty
  const totalTaxableAmount = round2(taxablePerUnit * qty);
  
  // 6. Calculate each tax separately on taxable amount
  const adjustedTaxes = baseTaxes.map(t => ({
    ...t,
    amount: round2(Math.max(0, totalTaxableAmount * (t.percent || 0) / 100))
  }));
  const totalTaxAmount = round2(adjustedTaxes.reduce((sum, t) => sum + t.amount, 0));
  
  // 7. Net = Taxable Amount + Taxes (ensure never negative)
  let newNetAmount = round2(Math.max(0, totalTaxableAmount + totalTaxAmount));
  
  // 8. Cap net amount to not exceed totalMRP (prevents rounding artifacts)
  const totalMRP = round2(item.baseMRP * qty);
  if (newNetAmount > totalMRP) {
    newNetAmount = totalMRP;
  }
  
  return {
    concessionPercent: newPercent,
    concessionAmount: round2(newAmount),
    totalSellingPrice: totalTaxableAmount,
    taxes: adjustedTaxes,
    netAmount: newNetAmount,
  };
};

/**
 * PharmacyItemsTable - Displays pharmacy items with billing fields
 * 
 * MEMOIZATION STRATEGY:
 * - Component is wrapped with React.memo for shallow prop comparison
 * - Internal calculations (usedBatchesByItem, totalBilledQtyByItem, cumulativeAmountBeforeRow)
 *   are memoized with [items] dependency
 * - Parent components should ensure `items` array reference is stable (only changes when
 *   items actually change). Formik's values.items provides this stability.
 * - Callback props (onFieldChange, onSelectChange, etc.) should be memoized with useCallback
 *   in parent to prevent unnecessary re-renders
 */
const PharmacyItemsTable: React.FC<PharmacyItemsTableProps> = ({
  items,
  onSelectChange,
  onSelectAll,
  onRemoveItem,
  onFieldChange,
  onItemUpdate,
  errors = [],
  touched = [],
  showDeleteAction = true,
  readOnly = false,
  showHeader = true,
  onQtyErrorChange,
  onItemReplace,
  clinicId,
  deptId,
  patientId,
  onPackageChange,
  disableBatchSelection = false,
  onDuplicateItem,
}) => {
  const { t: tBilling } = useTranslation('billing_config');
  const { currencySymbol } = useCurrency();
  
  // Memoized: O(n) iteration, recalculates only when items array reference changes
  // Calculate used batches per item (itemId -> Set of batchCodes)
  const usedBatchesByItem = useMemo(() => {
    const map = new Map<number, Set<string>>();
    items.forEach((item) => {
      const itemId = item.itemId;
      if (itemId && item.batchCode) {
        if (!map.has(itemId)) {
          map.set(itemId, new Set());
        }
        map.get(itemId)!.add(item.batchCode);
      }
    });
    return map;
  }, [items]);
  
  // Memoized: O(n) iteration, recalculates only when items array reference changes
  // Calculate total billed qty per item (sum across all rows with same itemId)
  const totalBilledQtyByItem = useMemo(() => {
    const map = new Map<number, number>();
    items.forEach((item) => {
      const itemId = item.itemId;
      if (itemId) {
        const current = map.get(itemId) || 0;
        map.set(itemId, current + (Number(item.billedQty) || 0));
      }
    });
    return map;
  }, [items]);
  
  // Memoized: O(n) iteration, recalculates only when items array reference changes
  // Calculate cumulative net amount per packageId in order
  // This tells each row how much package amount was consumed by rows BEFORE it
  // The package remaining amount is shared across ALL items using the same package
  const cumulativeAmountBeforeRow = useMemo(() => {
    const runningTotals = new Map<number, number>(); // packageId -> running total
    const result = new Map<number, number>(); // rowIndex -> cumulative amount BEFORE this row
    
    items.forEach((item, index) => {
      const pkgId = item.packageId;
      // Check if selected package has is_set_all_items
      const selectedPkg = pkgId && item.package_details 
        ? item.package_details.find(pkg => pkg.package_id === pkgId) 
        : null;
      const itemIsSetAllItems = selectedPkg?.is_set_all_items ?? false;
      // Include items from regular packages (is_fromPackage) OR is_set_all_items packages
      if (pkgId && (item.is_fromPackage || itemIsSetAllItems) && item.checked) {
        // Store cumulative BEFORE this row
        result.set(index, runningTotals.get(pkgId) || 0);
        // Add this row's amount to running total
        runningTotals.set(pkgId, (runningTotals.get(pkgId) || 0) + (Number(item.netAmount) || 0));
      }
    });
    return result;
  }, [items]);
  const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);
  
  // Check selection state for header checkbox
  const allSelected = items.length > 0 && items.every(item => item.checked);
  const someSelected = items.some(item => item.checked) && !allSelected;
  
  // Expose hasQtyError for parent to block submit
  const qtyError = hasAnyQtyError(items);
  
  // Notify parent of qtyError changes
  React.useEffect(() => {
    if (onQtyErrorChange) {
      onQtyErrorChange(qtyError);
    }
  }, [qtyError, onQtyErrorChange]);
  
  // Handle header checkbox click
  const handleSelectAllClick = () => {
    if (onSelectAll) {
      onSelectAll(!allSelected);
    }
  };
  
  if (items.length === 0) {
    return (
      <Box sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 1, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {tBilling('pharmacy_dispense.no_items_selected_from_prescribed', { defaultValue: 'No items selected from prescribed list yet. Go back to prescribed list and select items.' })}
        </Typography>
      </Box>
    );
  }

  const getFieldError = (index: number, field: string) => {
    return touched[index]?.[field] && errors[index]?.[field];
  };

  // Show Pkg Qty column if any item has a package_pharmacy_item_id OR if any selected package has is_set_all_items: true
  const showPkgQtyColumn = items.some(item => {
    if (item.package_pharmacy_item_id) return true;
    // Check if selected package has is_set_all_items: true
    if (item.packageId && item.package_details) {
      const selectedPackage = item.package_details.find(pkg => pkg.package_id === item.packageId);
      if (selectedPackage?.is_set_all_items) return true;
    }
    return false;
  });

  const showPackageColumn = items.some(item => (item.package_details && item.package_details.length > 0) || item.is_fromPackage || item.packageId);

  return (
    <TableContainer data-testid="dispense-items-table">
      <Table sx={{ borderCollapse: 'collapse', minWidth: 1980 }}>
        {showHeader && (
          <TableHead>
            <TableRow sx={tableHeaderRowStyles}>
              {/* Duplicate button header */}
              {onDuplicateItem && (
                <TableCell sx={{ width: 40, borderTopLeftRadius: 12, borderBottomLeftRadius: 12 }} />
              )}
              <TableCell padding="checkbox" sx={!onDuplicateItem ? { borderTopLeftRadius: 12, borderBottomLeftRadius: 12 } : undefined}>
                {onSelectAll && (
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    icon={<span className={styles.AutoCompleteDropDownIcon} />}
                    checkedIcon={<span className={styles.AutoCompleteDropDowncheckedIcon}>✔</span>}
                    onChange={handleSelectAllClick}
                    disableRipple
                    data-testid="dispense-items-selectall-checkbox"
                    inputProps={{ 'aria-label': tBilling('pharmacy_dispense.select_all_items', { defaultValue: 'Select all items' }) }}
                  />
                )}
              </TableCell>
              <TableCell sx={{ width: 100 }}>{tBilling('pharmacy_dispense.columns.visit_date', { defaultValue: 'Visit Date' })}</TableCell>
              <TableCell sx={{ width: 400 }}>{tBilling('pharmacy_dispense.columns.item_code_item_name', { defaultValue: 'Item Code / Item Name' })}</TableCell>
              {showPackageColumn && (
                <TableCell sx={{ width: 200 }}>{tBilling('pharmacy_dispense.columns.package', { defaultValue: 'Package' })}</TableCell>
              )}
              <TableCell sx={{ width: 180 }}>{tBilling('pharmacy_dispense.columns.batch_code_expiry', { defaultValue: 'Batch Code / Expiry Date' })}</TableCell>
              <TableCell sx={{ width: 120 }}>{tBilling('pharmacy_dispense.columns.manufacturer', { defaultValue: 'Manufacturer' })}</TableCell>
              <TableCell sx={{ width: 140 }}>{tBilling('pharmacy_dispense.columns.available_qty_uom', { defaultValue: 'Available Qty / UOM' })}</TableCell>
              {showPkgQtyColumn && (
                <TableCell sx={{ width: 180 }}>{tBilling('pharmacy_dispense.columns.pkg_total_pending_qty', { defaultValue: 'Pkg Total Qty / Pkg Pending Qty' })}</TableCell>
              )}
              <TableCell sx={{ width: 220 }}>{tBilling('pharmacy_dispense.columns.prescribed_strength_qty_remaining_uom', { defaultValue: 'Prescribed Strength / Qty. / Remaining Qty / UOM' })}</TableCell>
              <TableCell sx={{ width: 220 }}>{tBilling('pharmacy_dispense.columns.strength_billed_qty_uom', { defaultValue: 'Strength / Billed Qty. / UOM' })}</TableCell>
              <TableCell sx={{ width: 180 }}>{tBilling('pharmacy_dispense.columns.rack_shelf_container', { defaultValue: 'Rack / Shelf / Container' })}</TableCell>
            
              <TableCell sx={{ width: 200 }}>{tBilling('pharmacy_dispense.columns.mrp_price', { defaultValue: 'MRP Price' })}</TableCell>
              <TableCell sx={{ width: 200 }}>{tBilling('pharmacy_dispense.columns.concession_amount_percent_per_unit', { defaultValue: 'Concession Amount / (%) per Unit' })}</TableCell>
              <TableCell sx={{ width: 180 }}>{tBilling('pharmacy_dispense.columns.taxes_amount_percent', { defaultValue: 'Taxes Amount / (%)' })}</TableCell>
              <TableCell sx={{ width: 120 }}>{tBilling('pharmacy_dispense.columns.net_amount', { defaultValue: 'Net Amount' })}</TableCell>
              <TableCell sx={{ width: 100 }}>{tBilling('pharmacy_dispense.columns.pregnancy_class', { defaultValue: 'Pregnancy Class' })}</TableCell>
              <TableCell sx={{ width: 120 }}>{tBilling('pharmacy_dispense.columns.provider', { defaultValue: 'Provider' })}</TableCell>
           
              {showDeleteAction && (
                <TableCell sx={{ borderTopRightRadius: 12, borderBottomRightRadius: 12 }} />
              )}
            </TableRow>
          </TableHead>
        )}

        <TableBody>
          {items.map((item, index) => {

            // Validation: Qty should not exceed prescribedQty, availableStock, or effective package qty
            // Only show errors for checked items
            let qtyError = '';
            let qtyWarning = '';
            const billedQtyNum = Number(item.billedQty) || 0;
            const prescribedQtyNum = Number(item.prescribed_remaining_qty) || 0;
            const availableQtyNum = Number(item.availableStock);
            const isFromPackage = !!item.is_fromPackage && !!item.package_pharmacy_item_id;
            const pkgPendingQtyNum = isFromPackage && item.package_pending_qty != null ? Number(item.package_pending_qty) : null;
            
            // Check if selected package has is_set_all_items: true
            const selectedPackage = item.packageId && item.package_details 
              ? item.package_details.find(pkg => pkg.package_id === item.packageId) 
              : null;
            const isSetAllItems = selectedPackage?.is_set_all_items ?? false;
            
            // Calculate actual net price per unit (MRP with discount + taxes) for accurate package amount check
            const discountPercent = item.concessionPercent ?? 0;
            const itemTaxes = item.taxes || [];
            const netPricePerUnit = calculateNetPricePerUnit(item.baseMRP, discountPercent, itemTaxes);
            
            // Calculate effective package qty considering both quantity and amount constraints
            // Skip this calculation for is_set_all_items packages (no quantity_remaining validation)
            const effectivePkgQty = (!isSetAllItems && pkgPendingQtyNum !== null)
              ? calculateEffectivePackageQty(
                pkgPendingQtyNum,
                item.package_pharmacy_remaining_amount,
                netPricePerUnit
              )
              : null;
            
            // Only validate if item is checked
            if (item.checked) {
              if (billedQtyNum > prescribedQtyNum) {
                qtyError = tBilling('pharmacy_dispense.errors.qty_exceeds_prescribed_remaining', { defaultValue: 'Qty entered should not exceed prescribed remaining qty' });
              } else if (!isNaN(availableQtyNum) && billedQtyNum > availableQtyNum) {
                qtyError = tBilling('pharmacy_dispense.errors.qty_exceeds_available', { defaultValue: 'Qty entered should not exceed available qty' });
              } else if (!isSetAllItems && isFromPackage && effectivePkgQty !== null && billedQtyNum > effectivePkgQty) {
                // Skip quantity_remaining validation if is_set_all_items is true
                const exceedBy = billedQtyNum - effectivePkgQty;
                // Show warning - exceeded qty will be billed as non-package
                if (pkgPendingQtyNum !== null && effectivePkgQty < pkgPendingQtyNum) {
                  qtyWarning = tBilling('pharmacy_dispense.warnings.qty_exceeds_package_pending_with_covered_units', {
                    defaultValue: 'Qty entered exceeds package pending qty by {{exceedBy}} (only {{coveredUnits}} units covered by remaining amount). Excess will be billed separately.',
                    exceedBy,
                    coveredUnits: effectivePkgQty,
                  });
                } else {
                  qtyWarning = tBilling('pharmacy_dispense.warnings.qty_exceeds_package_pending', {
                    defaultValue: 'Qty entered exceeds package pending qty by {{exceedBy}}. Excess will be billed separately.',
                    exceedBy,
                  });
                }
              }
            }
            // Check if this item can be duplicated (has more than 1 batch available)
            const availableBatchCount = item.availableBatches?.length || 0;
            const usedBatchesForItem = item.itemId ? usedBatchesByItem.get(item.itemId) : new Set<string>();
            const unusedBatchCount = availableBatchCount - (usedBatchesForItem?.size || 0);
            const canDuplicate = availableBatchCount > 1 && unusedBatchCount > 0;
            
            // Calculate remaining qty for this item across all rows (for duplicates validation)
            const totalQtyForItem = item.itemId ? totalBilledQtyByItem.get(item.itemId) || 0 : 0;
            const maxTotalQty = Number(item.prescribed_remaining_qty) || 0;
            
            // Calculate remaining qty that can be entered in this row (considering other rows with same item)
            const currentRowQty = Number(item.billedQty) || 0;
            const otherRowsQty = totalQtyForItem - currentRowQty;
            const remainingQtyForThisRow = Math.max(0, maxTotalQty - otherRowsQty);
            
            // Check if total qty across duplicates exceeds prescribed limit
            let duplicateQtyError = '';
            if (item.checked && maxTotalQty > 0 && totalQtyForItem > maxTotalQty) {
              duplicateQtyError = tBilling('pharmacy_dispense.errors.total_qty_exceeds_prescribed_remaining', {
                defaultValue: 'Total qty across all lines ({{totalQty}}) exceeds prescribed remaining qty ({{maxQty}})',
                totalQty: totalQtyForItem,
                maxQty: maxTotalQty,
              });
            }
            
            // Check if THIS row's net amount exceeds the remaining package amount (after previous rows consumed their share)
            // The remaining amount is shared across ALL items using the same packageId
            // For is_set_all_items packages, this is the primary validation (no quantity_remaining check)
            let packageAmountWarning = '';
            const pkgId = item.packageId;
            const pkgRemainingAmount = Number(item.package_pharmacy_remaining_amount) || 0;
            // Include is_set_all_items packages in amount validation (even without package_pharmacy_item_id)
            const shouldValidatePackageAmount = pkgId && item.checked && pkgRemainingAmount > 0 && (isFromPackage || isSetAllItems);
            if (shouldValidatePackageAmount) {
              const amountConsumedBefore = cumulativeAmountBeforeRow.get(index) || 0;
              const remainingForThisRow = Math.max(0, pkgRemainingAmount - amountConsumedBefore);
              const thisRowAmount = Number(item.netAmount) || 0;
              const thisRowQty = Number(item.billedQty) || 0;
              
              if (thisRowAmount > remainingForThisRow && thisRowQty > 0) {
                // Calculate how many units can be covered by remaining package amount
                const pricePerUnit = thisRowAmount / thisRowQty;
                const qtyCoveredByPackage = pricePerUnit > 0 ? Math.floor(remainingForThisRow / pricePerUnit) : 0;
                const qtyBilledSeparately = thisRowQty - qtyCoveredByPackage;
                const amountBilledSeparately = (qtyBilledSeparately * pricePerUnit).toFixed(2);
                
                packageAmountWarning = tBilling('pharmacy_dispense.warnings.package_amount_split_billing', {
                  defaultValue: '{{qtyBilledSeparately}} unit(s) ({{currencySymbol}}{{amountBilledSeparately}}) will be billed separately. Package covers only {{qtyCoveredByPackage}} unit(s) from remaining {{currencySymbol}}{{remainingForThisRow}}.',
                  qtyBilledSeparately,
                  currencySymbol,
                  amountBilledSeparately,
                  qtyCoveredByPackage,
                  remainingForThisRow: remainingForThisRow.toFixed(2),
                });
              }
            }
            
            return(
              <TableRow
                key={index}
                sx={{
                  background: '#FFFFFF',
                  '&:hover': {
                    backgroundColor: '#F9FAFB',
                  },
                  '& td': {
                    borderTop: index === 0 ? 'none' : undefined,
                    borderBottom: index === items.length - 1 ? 'none' : undefined,
                    paddingY: 1.25,
                  },
                }}
              >
                {/* Duplicate Button */}
                {onDuplicateItem && (
                  <TableCell sx={{ width: 40, borderTopLeftRadius: 12, borderBottomLeftRadius: 12 }}>
                    <Tooltip 
                      title={
                        !canDuplicate 
                          ? availableBatchCount <= 1 
                            ? tBilling('pharmacy_dispense.cannot_duplicate_single_batch', { defaultValue: 'Cannot duplicate. Only one batch available.' })
                            : tBilling('pharmacy_dispense.cannot_duplicate_all_batches_used', { defaultValue: 'Cannot duplicate. All batches already in use.' })
                          : tBilling('pharmacy_dispense.duplicate_item_with_different_batch', { defaultValue: 'Duplicate item with different batch' })
                      }
                    >
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => canDuplicate && onDuplicateItem?.(index)}
                          disabled={!canDuplicate || readOnly}
                          data-testid={`dispense-item-${item.itemCode}-duplicate-button`}
                          sx={{
                            p: 0.5,
                            color: canDuplicate ? '#3A80E2' : '#D0D5DD',
                            cursor: canDuplicate ? 'pointer' : 'not-allowed',
                            backgroundColor: canDuplicate ? 'rgba(90, 138, 234, 0.1)' : 'transparent',
                            '&:hover': {
                              backgroundColor: canDuplicate ? 'rgba(90, 138, 234, 0.2)' : 'transparent',
                            },
                            '&.Mui-disabled': {
                              color: '#D0D5DD',
                              pointerEvents: 'auto',
                              cursor: 'not-allowed',
                            },
                          }}
                        >
                          {canDuplicate ? <CopyIcon /> : <CopyIconDisabled />}
                        </IconButton>
                      </Box>
                    </Tooltip>
                  </TableCell>
                )}
                {/* Checkbox */}
                <TableCell padding="checkbox" sx={!onDuplicateItem ? { borderTopLeftRadius: 12, borderBottomLeftRadius: 12 } : undefined}>
                  <Checkbox
                    checked={item.checked}
                    onChange={() => onSelectChange(index)}
                    icon={<span className={styles.AutoCompleteDropDownIcon} />}
                    checkedIcon={<span className={styles.AutoCompleteDropDowncheckedIcon}>✔</span>}
                    disableRipple
                    data-testid={`dispense-item-${item.itemCode}-checkbox`}
                    inputProps={{
                      'aria-label': tBilling('pharmacy_dispense.select_item', {
                        defaultValue: 'Select {{itemName}}',
                        itemName: item.itemName,
                      })
                    }}
                  />
                </TableCell>

                {/* Visit Date */}
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography variant="body2">{item.visitDate}</Typography>
                </TableCell>

                {/* Item Code / Name */}
                <TableCell>
                  <Box>
                    {/* Show ItemSearchField when in edit mode */}
                    {!readOnly && onItemReplace && editingRowIndex === index ? (
                      // Edit mode - show search field
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 500 }}>
                            {tBilling('pharmacy_dispense.search_add_item', { defaultValue: 'Search and add item:' })}
                          </Typography>
                          <IconButton 
                            size="small" 
                            onClick={() => setEditingRowIndex(null)}
                            sx={{ p: 0.5 }}
                          >
                            <CloseIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                        <ItemSearchField
                          onSelectItem={(selectedItem) => {
                            onItemReplace(index, selectedItem);
                            setEditingRowIndex(null);
                          }}
                          clinicId={clinicId}
                          deptId={deptId}
                          patientId={patientId}
                          packageId={item.is_fromPackage || item.packageId ? item.packageId : null}
                        />
                        <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                          {tBilling('pharmacy_dispense.current_item', { defaultValue: 'Current' })}: {item.itemCode} - {item.itemName}
                        </Typography>
                      </Box>
                    ) : item.availableBatches?.length === 0 ? (
                      // No batches available - show warning with edit icon
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {(item.prescriptionId ?? 0) > 0 && (
                            <Box component="span" sx={{ color: 'error.main', fontWeight: 500, fontSize: 12 }}>
                              (Rx)
                              
                            </Box>
                          )}
                          <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 500 }}>
                            {tBilling('pharmacy_dispense.no_stock_select_substitute', { defaultValue: 'No stock - Select substitute' })}
                          </Typography>
                          {onItemReplace && (
                            <Tooltip title={tBilling('pharmacy_dispense.search_add_item', { defaultValue: 'Search and add item:' })}>
                              <IconButton 
                                size="small" 
                                onClick={() => setEditingRowIndex(index)}
                                sx={{ 
                                  p: 0.5,
                                  backgroundColor: 'transparent',
                                  '&:hover': { 
                                    backgroundColor: 'rgba(90, 138, 234, 0.1)', 
                                  }
                                }}
                              >
                                <EditOutlinedIcon sx={{ fontSize: 18, color: '#3A80E2' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                          Item Code: {item.itemCode} - {item.itemName}
                        </Typography>
                      </Box>
                    ) : (
                      // Normal mode - show item info with optional edit icon
                      <>
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap' }}>
                          {(item.prescriptionId ?? 0) > 0 && (
                            <Box component="span" sx={{ color: 'error.main', fontWeight: 500 }}>
                              (Rx)
                              {' '}
                            </Box>
                          )}
                          {tBilling('pharmacy_dispense.item_code', { defaultValue: 'Item Code' })} : {item.itemCode}
                          {item.is_fromPackage && (
                            <Box component="span" sx={{ ml: 1, whiteSpace: 'nowrap' }}>
                              <span
                                style={{
                                  display: 'inline-block',
                                  background: '#E0F2F1',
                                  color: '#00796B',
                                  borderRadius: 8,
                                  padding: '2px 8px',
                                  fontSize: 12,
                                  fontWeight: 500,
                                  marginLeft: 4,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {tBilling('pharmacy_dispense.from_package', { defaultValue: 'From Package' })}
                              </span>
                            </Box>
                          )}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {item.itemName}
                        </Typography>
                      </>
                    )}
                  </Box>
                </TableCell>

                {/* Package Selection */}
                {showPackageColumn && (
                  <TableCell>
                    {readOnly ? (
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.packageName || '-'}
                      </Typography>
                    ) : item.package_details && item.package_details.length > 0 ? (
                      <Autocomplete
                        size="small"
                        options={item.package_details}
                        value={item.package_details.find(pkg => pkg.package_id === item.packageId) || null}
                        onChange={(_event, newValue) => {
                          // Calculate concession values based on package discount_percent
                          const discountPercent = newValue?.discount_percent ?? 0;
                          const concessionUpdates = calculateConcessionValues(item, discountPercent);
                          
                          if (onPackageChange) {
                            onPackageChange(index, newValue);
                            // Also update concession values
                            if (onItemUpdate) {
                              onItemUpdate(index, concessionUpdates);
                            }
                          } else if (onItemUpdate) {
                            onItemUpdate(index, {
                              packageId: newValue ? newValue.package_id : null,
                              packageName: newValue?.package_name || undefined,
                              is_fromPackage: !!newValue,
                              package_pharmacy_item_id: newValue?.package_pharmacy_item_id ?? undefined,
                              package_pending_qty: newValue?.quantity_remaining ?? undefined,
                              package_total_qty: newValue?.total_quantity ?? undefined,
                              uom_description: newValue?.uom_description ?? undefined,
                              package_pharmacy_remaining_amount: newValue?.pharmacy_remaining_amount,
                              package_unit_price: newValue?.unit_price,
                              // Include concession values
                              ...concessionUpdates,
                            });
                          }
                        }}
                        getOptionLabel={(option) => option.package_name}
                        isOptionEqualToValue={(option, value) => option.package_id === value.package_id}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder={tBilling('pharmacy_dispense.select_package', { defaultValue: 'Select Package' })}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 3,
                                '& fieldset': { borderColor: '#E4E7EC' },
                                '&:hover fieldset': { borderColor: '#D0D5DD' },
                                '&.Mui-focused fieldset': {
                                  borderColor: '#5A8AEA',
                                  boxShadow: '0 0 0 1px rgba(90, 138, 234, 0.15)',
                                },
                              },
                            }}
                          />
                        )}
                        sx={{ minWidth: 180 }}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {item.packageName || '-'}
                      </Typography>
                    )}
                  </TableCell>
                )}

                {/* Batch Code / Expiry Date */}
                <TableCell>
                  <Box>
                    {readOnly ? (
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.batchCode}
                      </Typography>
                    ) : (
                      <FormControl fullWidth size="small" sx={{ mb: 0.5 }} error={!!getFieldError(index, 'batchCode')}>
                        <Select
                          value={item.batchCode}
                          error={!!getFieldError(index, 'batchCode')}
                          disabled={disableBatchSelection || item.availableBatches?.length === 0}
                          data-testid={`dispense-item-${item.itemCode}-batch-select`}
                          inputProps={{
                            'aria-label': tBilling('pharmacy_dispense.select_batch_for', {
                              defaultValue: 'Select batch for {{itemName}}',
                              itemName: item.itemName,
                            })
                          }}
                          onChange={(e) => {
                            const selectedBatchCode = e.target.value;
                            const selectedBatch = item.availableBatches?.find(
                              (b) => b.batchNumber === selectedBatchCode
                            );
                          
                            // Use batch update if available
                            if (onItemUpdate && selectedBatch) {
        
                              // Use converted prices from UOM conversion if available, otherwise use batch base prices
                              const newBaseMRP = selectedBatch.baseMrp ?? 0;
                              const newBaseCostPrice = selectedBatch.baseCostPrice ?? 0;
                              const qty = item.billedQty ?? 1; // Use existing qty or default to 1
                              const concessionPercent = item.concessionPercent || 0;
                              
                              // Map taxes from batch to format expected by calcFinancials
                              const itemTaxes = (selectedBatch.taxes || []).map(t => ({
                                tax_name: t.name,
                                tax_percentage: t.percent,
                              }));
                              
                              // Use calcFinancials for consistent calculation with discount
                              const { totalMRP, sellingPricePerUnit, discount, taxesWithAmounts, netAmount } = calcFinancials(
                                newBaseMRP, qty, concessionPercent, itemTaxes
                              );
                              
                              // Map taxes back to UI format
                              const mappedTaxes = taxesWithAmounts.map(t => ({
                                name: t.tax_name,
                                percent: t.tax_percentage,
                                amount: t.tax_amount,
                              }));
                              
                              onItemUpdate(index, {
                                batchCode: selectedBatchCode,
                                batchId: selectedBatch.batchId,
                                expiryDate: selectedBatch.expiryDate,
                                baseMRP: newBaseMRP,
                                baseCostPrice: newBaseCostPrice,
                                billedQty: qty,
                                totalMRPAmount: totalMRP,
                                sellingPricePerUnit: sellingPricePerUnit,
                                concessionAmount: discount,
                                storeId: selectedBatch.storeId,
                                availableStock: selectedBatch.availableStock,
                                availableUomId: selectedBatch.availableUomId,
                                availableUom: selectedBatch.availableUom,
                                billedUOM: selectedBatch.availableUom ?? '',
                                taxes: mappedTaxes,
                                netAmount: netAmount,
                              });
                            } else {
                              onFieldChange(index, 'batchCode', selectedBatchCode);
                              if (selectedBatch) {
                                onFieldChange(index, 'expiryDate', selectedBatch.expiryDate);
                              }
                            }
                          }}
                          displayEmpty
                          renderValue={(selected) => {
                            if (item.availableBatches?.length === 0) {
                              return <Typography sx={{ color: 'warning.main' }}>{tBilling('pharmacy_dispense.no_batches_available', { defaultValue: 'No batches available' })}</Typography>;
                            }
                            if (!selected) {
                              return <Typography sx={{ color: 'text.disabled' }}>{tBilling('pharmacy_dispense.select_batch_code', { defaultValue: 'Select Batch Code' })}</Typography>;
                            }
                            return selected;
                          }}
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderRadius: 3,
                              borderColor: '#E4E7EC',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#D0D5DD',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#5A8AEA',
                              boxShadow: '0 0 0 1px rgba(90, 138, 234, 0.15)',
                            },
                          }}
                        >
                          {item.availableBatches?.length > 0 ? (
                            item.availableBatches.map((batch) => {
                              // Check if this batch is already used by another row of the same item
                              const isBatchUsedElsewhere = Boolean(item.itemId && usedBatchesForItem?.has(batch.batchNumber) && batch.batchNumber !== item.batchCode);
                              const isDisabled = Boolean(batch.isExpired) || isBatchUsedElsewhere;
                              return (
                                <MenuItem 
                                  key={batch.batchNumber} 
                                  value={batch.batchNumber}
                                  disabled={isDisabled}
                                  sx={{
                                    color: batch.isExpired ? 'error.main' : isBatchUsedElsewhere ? 'text.disabled' : 'inherit',
                                    opacity: isDisabled ? 0.6 : 1,
                                  }}
                                >
                                  {batch.batchNumber}
                                  {batch.isExpired && ` (${tBilling('pharmacy_dispense.expired', { defaultValue: 'Expired' })})`}
                                  {isBatchUsedElsewhere && ` (${tBilling('pharmacy_dispense.already_selected', { defaultValue: 'Already selected' })})`}
                                </MenuItem>
                              );
                            })
                          ) : (
                            <MenuItem value="" disabled>
                              <Typography sx={{ color: 'text.disabled' }}>{tBilling('pharmacy_dispense.no_batches_available', { defaultValue: 'No batches available' })}</Typography>
                            </MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    )}
                    {item.expiryDate && (
                      <Typography variant="caption" color="text.secondary">
                        {tBilling('pharmacy_dispense.expiry_date', { defaultValue: 'Expiry Date' })} : {item.expiryDate}
                      </Typography>
                    )}
                  </Box>
                </TableCell>

                {/* Manufacturer */}
                <TableCell>
                  <Typography variant="body2">{item.manufacturer || '-'}</Typography>
                </TableCell>


                {/* Available Qty / UOM */}
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {item.availableStock !== undefined && item.availableStock !== null
                      ? `${item.availableStock} ${item.availableUom || ''}`
                      : '-'}
                  </Typography>
                </TableCell>

                {/* Pkg Total Qty / Pkg Pending Qty */}
                {showPkgQtyColumn && (
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {item.package_pharmacy_item_id
                        ? `${item.package_total_qty}  ${item.uom_description ?? '-'} / ${item.package_pending_qty}  ${item.uom_description ?? '-'}`
                        : '-'}
                    </Typography>
                  </TableCell>
                )}

                {/* Prescribed Strength / Qty / UOM - READ ONLY DISPLAY */}
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {item.prescribedStrength && item.prescribedQty
                      ? `${item.prescribedStrength} / ${item.prescribedQty} / ${item.prescribed_remaining_qty} ${item.prescribedUOM}`
                      : '-'}
                  </Typography>
                </TableCell>

                {/* Billed Strength / Qty / UOM - EDITABLE */}
                <TableCell>
                  {readOnly ? (
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {item.billedStrength || '-'} / {item.billedQty} {item.billedUOM}
                    </Typography>
                  ) : (
                    <>
                      <Box sx={{ display: 'flex', gap: 0.75, mb: 0.75 }}>
                        <TextField
                          size="small"
                          label={tBilling('pharmacy_dispense.strength', { defaultValue: 'Strength' })}
                          type="text"
                          value={item.billedStrength}
                          onChange={(e) => onFieldChange(index, 'billedStrength', e.target.value)}
                          placeholder={tBilling('pharmacy_dispense.strength_example', { defaultValue: 'e.g. 1-2-1' })}
                          error={!!getFieldError(index, 'billedStrength')}
                          disabled
                          sx={{
                            flex: 1,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 3,
                              '& fieldset': { borderColor: '#E4E7EC' },
                              '&:hover fieldset': { borderColor: '#D0D5DD' },
                              '&.Mui-focused fieldset': {
                                borderColor: '#5A8AEA',
                                boxShadow: '0 0 0 1px rgba(90, 138, 234, 0.15)',
                              },
                            },
                          }}
                          inputProps={{ maxLength: 50 }}
                        />

                        <TextField
                          size="small"
                          label={tBilling('pharmacy_dispense.qty', { defaultValue: 'Qty' })}
                          type="number"
                          value={item.billedQty || ''}
                          data-testid={`dispense-item-${item.itemCode}-qty-input`}
                          inputProps={{ min: 0, step: 1, 'aria-label': `Quantity for ${item.itemName}` }}
                          onChange={(e) => {
                            const newQty = Number(e.target.value) || 0;
                            const concessionPercent = item.concessionPercent || 0;
                            
                            // Map taxes to format expected by calcFinancials
                            const itemTaxes = (item.taxes || []).map(t => ({
                              tax_name: t.name,
                              tax_percentage: t.percent,
                            }));
                            
                            // Use calcFinancials for consistent calculation with discount
                            const { totalMRP, sellingPricePerUnit, discount, taxesWithAmounts, netAmount } = calcFinancials(
                              item.baseMRP, newQty, concessionPercent, itemTaxes
                            );
                            
                            // Map taxes back to UI format
                            const mappedTaxes = taxesWithAmounts.map(t => ({
                              name: t.tax_name,
                              percent: t.tax_percentage,
                              amount: t.tax_amount,
                            }));

                            // Use batch update if available, otherwise fall back to single updates
                            if (onItemUpdate) {
                              onItemUpdate(index, {
                                billedQty: newQty,
                                totalMRPAmount: totalMRP,
                                sellingPricePerUnit: sellingPricePerUnit,
                                concessionAmount: discount,
                                taxes: mappedTaxes,
                                netAmount: netAmount,
                              });
                            } else {
                              onFieldChange(index, 'billedQty', newQty);
                            }
                          }}
                          placeholder={tBilling('pharmacy_dispense.zero', { defaultValue: '0' })}
                          error={!!getFieldError(index, 'billedQty') || !!qtyError}
                          disabled={!item.batchCode}
                          sx={{
                            flex: 1,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 3,
                              '& fieldset': { borderColor: '#E4E7EC' },
                              '&:hover fieldset': { borderColor: '#D0D5DD' },
                              '&.Mui-focused fieldset': {
                                borderColor: '#5A8AEA',
                                boxShadow: '0 0 0 1px rgba(90, 138, 234, 0.15)',
                              },
                            },
                          }}
                        />

                      </Box>

                      <FormControl fullWidth size="small" error={!!getFieldError(index, 'billedUOM')}>
                        <Select
                          value={item.billedUOM}
                          data-testid={`dispense-item-${item.itemCode}-uom-select`}
                          inputProps={{ 'aria-label': `UOM for ${item.itemName}` }}
                          onChange={(e) => {
                            const selectedUomCode = e.target.value;
                            const selectedUom = item.uomConversions?.find(
                              (uom) => uom.uom_code === selectedUomCode
                            );
                          
                            if (onItemUpdate && selectedUom) {
                              // Update UOM and recalculate prices based on conversion
                              const convertedMRP = selectedUom.converted_mrp;
                              const convertedCost = selectedUom.converted_cost_price;
                              const concessionPercent = item.concessionPercent || 0;
                              
                              // Map taxes to format expected by calcFinancials
                              const itemTaxes = (item.taxes || []).map(t => ({
                                tax_name: t.name,
                                tax_percentage: t.percent,
                              }));
                              
                              // Use calcFinancials for consistent calculation with discount
                              const { totalMRP, sellingPricePerUnit, discount, taxesWithAmounts, netAmount } = calcFinancials(
                                convertedMRP, item.billedQty, concessionPercent, itemTaxes
                              );
                              
                              // Map taxes back to UI format
                              const mappedTaxes = taxesWithAmounts.map(t => ({
                                name: t.tax_name,
                                percent: t.tax_percentage,
                                amount: t.tax_amount,
                              }));
                            
                              onItemUpdate(index, {
                                billedUOM: selectedUomCode,
                                baseMRP: convertedMRP,
                                baseCostPrice: convertedCost,
                                totalMRPAmount: totalMRP,
                                sellingPricePerUnit: sellingPricePerUnit,
                                concessionAmount: discount,
                                taxes: mappedTaxes,
                                netAmount: netAmount,
                              });
                            } else {
                              onFieldChange(index, 'billedUOM', selectedUomCode);
                            }
                          }}
                          error={!!getFieldError(index, 'billedUOM')}
                          disabled={!item.batchCode}
                          displayEmpty
                          renderValue={(selected) => {
                            if (!selected) {
                              return <Typography sx={{ color: 'text.disabled' }}>{tBilling('pharmacy_dispense.select_uom', { defaultValue: 'Select UOM' })}</Typography>;
                            }
                            return selected;
                          }}
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderRadius: 3,
                              borderColor: '#E4E7EC',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#D0D5DD',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#5A8AEA',
                              boxShadow: '0 0 0 1px rgba(90, 138, 234, 0.15)',
                            },
                          }}
                        >
                          {item.uomConversions && item.uomConversions.length > 0 ? (
                            item.uomConversions.map((uom) => (
                              <MenuItem key={uom.id} value={uom.uom_code}>
                                {uom.uom_code}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem value="" disabled>
                              <Typography sx={{ color: 'text.disabled' }}>{tBilling('pharmacy_dispense.no_uom_conversions_available', { defaultValue: 'No UOM conversions available' })}</Typography>
                            </MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    </>
                  )}
                  {qtyError && (
                    <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, display: 'block' }}>
                      {qtyError}
                    </Typography>
                  )}
                  {duplicateQtyError && !qtyError && (
                    <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, display: 'block' }}>
                      {duplicateQtyError}
                    </Typography>
                  )}
                  {qtyWarning && !qtyError && !duplicateQtyError && !packageAmountWarning && (
                    <Typography variant="caption" sx={{ color: 'warning.main', mt: 0.5, display: 'block' }}>
                      {qtyWarning}
                    </Typography>
                  )}
                  {packageAmountWarning && !qtyError && !duplicateQtyError && (
                    <Typography variant="caption" sx={{ color: 'warning.main', mt: 0.5, display: 'block' }}>
                      {packageAmountWarning}
                    </Typography>
                  )}
                  {maxTotalQty > 0 && remainingQtyForThisRow < maxTotalQty && !qtyError && !duplicateQtyError && !packageAmountWarning && !qtyWarning && (
                    <Typography variant="caption" sx={{ color: 'info.main', mt: 0.5, display: 'block' }}>
                      {tBilling('pharmacy_dispense.max_qty_for_row', {
                        defaultValue: 'Max qty for this row: {{remainingQty}} (other rows have {{otherRowsQty}})',
                        remainingQty: remainingQtyForThisRow,
                        otherRowsQty,
                      })}
                    </Typography>
                  )}
                </TableCell>

                {/* Rack / Shelf / Container */}
                <TableCell>
                  <Typography variant="body2" sx={{ fontSize: 13 }}>
                    <Box component="span" sx={{ color: 'text.secondary' }}>{tBilling('pharmacy_dispense.rack', { defaultValue: 'Rack' })}:</Box> {item.rack || '-'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: 13, mt: 0.25 }}>
                    <Box component="span" sx={{ color: 'text.secondary' }}>{tBilling('pharmacy_dispense.shelf', { defaultValue: 'Shelf' })}:</Box> {item.shelf || '-'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: 13, mt: 0.25 }}>
                    <Box component="span" sx={{ color: 'text.secondary' }}>{tBilling('pharmacy_dispense.container', { defaultValue: 'Container' })}:</Box> {item.container || '-'}
                  </Typography>
                </TableCell>

                {/* MRP Price */}
                <TableCell sx={{ width: 200, minWidth: 200 }}>
                  <Typography 
                    variant="body2" 
                    display="block" 
                    sx={{ fontSize: 13, fontWeight: 400, color: '#667085' }}
                  >
                    {tBilling('pharmacy_dispense.base_mrp', { defaultValue: 'Base MRP' })}: {currencySymbol}{item.baseMRP.toFixed(2)}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    display="block" 
                    sx={{ fontSize: 13, fontWeight: 400, color: '#667085' }}
                  >
                    {tBilling('pharmacy_dispense.base_cost', { defaultValue: 'Base Cost' })}: {currencySymbol}{(item.baseCostPrice || 0).toFixed(2)}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 500, 
                      color: '#101828', 
                      fontSize: 14, 
                      mt: 0.5 
                    }}
                  >
                    {tBilling('pharmacy_dispense.total_mrp', { defaultValue: 'Total MRP' })}: {currencySymbol}{item.totalMRPAmount.toFixed(2)}
                  </Typography>
                </TableCell>

                {/* Concession Amount / (%) */}
                <TableCell sx={{ width: 200, minWidth: 200 }}>
                  {readOnly ? (
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {currencySymbol}{(item.concessionAmount || 0).toFixed(2)} | {(item.concessionPercent || 0).toFixed(2)}%
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {/* Row 1: Total Concession Amount Input */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontSize: 13, 
                            fontWeight: 400, 
                            color: '#667085',
                            minWidth: 50,
                          }}
                        >
                          {tBilling('pharmacy_dispense.amount', { defaultValue: 'Amount' })}:
                        </Typography>
                        <Tooltip 
                          title={item.packageId ? tBilling('pharmacy_dispense.concession_auto_set_package', { defaultValue: 'Concession is auto-set from package discount' }) : ''} 
                          placement="top"
                          disableHoverListener={!item.packageId}
                        >
                          <TextField
                            size="small"
                            type="number"
                            value={item.concessionAmount ?? 0}
                            disabled={!!item.packageId}
                            data-testid={`dispense-item-${item.itemCode}-concession-amount-input`}
                            onChange={(e) => {
                              const newAmount = parseFloat(e.target.value) || 0;
                              const updates = calculateConcessionFromAmount(item, newAmount);
                              
                              if (onItemUpdate) {
                                onItemUpdate(index, updates);
                              } else {
                                onFieldChange(index, 'concessionAmount', newAmount);
                              }
                            }}
                            inputProps={{ 
                              min: 0, 
                              step: 0.01,
                              'aria-label': `Concession amount for ${item.itemName}`,
                              style: { textAlign: 'right' }
                            }}
                            sx={{ 
                              width: 80,
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                '& fieldset': { borderColor: '#E4E7EC' },
                                '&:hover fieldset': { borderColor: '#D0D5DD' },
                                '&.Mui-focused fieldset': {
                                  borderColor: '#5A8AEA',
                                  boxShadow: '0 0 0 1px rgba(90, 138, 234, 0.15)',
                                },
                                '&.Mui-disabled': {
                                  backgroundColor: '#F9FAFB',
                                  '& fieldset': { borderColor: '#E4E7EC' },
                                },
                              },
                              '& input': { py: 0.5, px: 0.75, fontSize: 14 },
                            }}
                          />
                        </Tooltip>
                      </Box>
                      {/* Row 2: Concession Percent Input */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontSize: 13, 
                            fontWeight: 400, 
                            color: '#667085',
                            minWidth: 50,
                          }}
                        >
                          {tBilling('pharmacy_dispense.percent', { defaultValue: 'Percent' })}%:
                        </Typography>
                        <Tooltip 
                          title={item.packageId ? tBilling('pharmacy_dispense.concession_auto_set_package', { defaultValue: 'Concession is auto-set from package discount' }) : ''} 
                          placement="top"
                          disableHoverListener={!item.packageId}
                        >
                          <TextField
                            size="small"
                            type="number"
                            value={item.concessionPercent ?? 0}
                            disabled={!!item.packageId}
                            data-testid={`dispense-item-${item.itemCode}-concession-percent-input`}
                            onChange={(e) => {
                              const newPercent = parseFloat(e.target.value) || 0;
                              const updates = calculateConcessionValues(item, newPercent);
                              
                              if (onItemUpdate) {
                                onItemUpdate(index, updates);
                              } else {
                                onFieldChange(index, 'concessionPercent', newPercent);
                              }
                            }}
                            inputProps={{ 
                              min: 0, 
                              max: 100, 
                              step: 0.01,
                              'aria-label': `Concession percent for ${item.itemName}`,
                              style: { textAlign: 'right' }
                            }}
                            sx={{ 
                              width: 80,
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                '& fieldset': { borderColor: '#E4E7EC' },
                                '&:hover fieldset': { borderColor: '#D0D5DD' },
                                '&.Mui-focused fieldset': {
                                  borderColor: '#5A8AEA',
                                  boxShadow: '0 0 0 1px rgba(90, 138, 234, 0.15)',
                                },
                                '&.Mui-disabled': {
                                  backgroundColor: '#F9FAFB',
                                  '& fieldset': { borderColor: '#E4E7EC' },
                                },
                              },
                              '& input': { py: 0.5, px: 0.75, fontSize: 14 },
                            }}
                          />
                        </Tooltip>
                      </Box>
                    </Box>
                  )}
                </TableCell>

                {/* Taxes (Dynamic - from backend taxes array) */}
                <TableCell sx={{ width: 180, minWidth: 180 }}>
                  {(() => {
                    // Use taxes array from backend
                    const taxes = item.taxes && item.taxes.length > 0 
                      ? item.taxes.filter(tax => (tax.percent ?? 0) > 0)
                      : [];

                    if (taxes.length === 0) {
                      return (
                        <Typography 
                          variant="body2" 
                          sx={{ fontSize: 13, color: '#667085' }}
                        >
                          -
                        </Typography>
                      );
                    }

                    return taxes.map((tax, idx) => (
                      <Typography 
                        key={idx}
                        variant="body2" 
                        display="block" 
                        sx={{ fontSize: 13, fontWeight: 400, color: '#667085' }}
                      >
                        {tax.name}: {currencySymbol}{(tax.amount || 0).toFixed(2)} ({tax.percent}%)
                      </Typography>
                    ));
                  })()}
                </TableCell>

                {/* Net Amount */}
                <TableCell sx={{ width: 120, minWidth: 120 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 600, 
                      color: '#101828', 
                      fontSize: 14 
                    }}
                  >
                    {currencySymbol}{item.netAmount.toFixed(2)}
                  </Typography>
                </TableCell>

                {/* Pregnancy Class */}
                <TableCell sx={{ minWidth: 100 }}>
                  <Typography variant="body2" sx={{ color: '#667085' }}>
                    {item.pregnancyClass || '-'}
                  </Typography>
                </TableCell>

                {/* Provider */}
                <TableCell sx={{ minWidth: 120 }}>
                  <Typography variant="body2" sx={{ color: '#667085', whiteSpace: 'nowrap' }}>
                    {item.provider || '-'}
                  </Typography>
                </TableCell>


                {/* Delete Action */}
                {showDeleteAction && onRemoveItem && (
                  <TableCell sx={{ borderTopRightRadius: 12, borderBottomRightRadius: 12, width: 64 }}>
                    <Tooltip title={tBilling('pharmacy_dispense.remove', { defaultValue: 'Remove' })}>
                      <IconButton
                        size="small"
                        onClick={() => onRemoveItem(index)}
                        data-testid={`dispense-item-${item.itemCode}-delete-button`}
                        aria-label={`${tBilling('pharmacy_dispense.remove', { defaultValue: 'Remove' })} ${item.itemName}`}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// React.memo prevents re-renders when props haven't changed (shallow comparison)
// Parent should ensure stable references for items array and callback props 
export default React.memo(PharmacyItemsTable);
